import React, {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useContext
} from 'react';

import ToastContainer from '../components/Toast/ToastContainer';

type ToastType = 'info' | 'success' | 'warning' | 'error';

type ToastConfig = {
  type?: ToastType;
  autoClose?: number;
  closeOnClick?: boolean;
  icon?: string | React.ComponentType;
  actionText?: string;
  onActionClick?: () => void;
  filled?: boolean;
};

type ToastItem = {
  id: string;
  message: string;
  config: Required<
    Omit<ToastConfig, 'icon' | 'actionText' | 'onActionClick' | 'filled'>
  > & {
    icon?: ToastConfig['icon'];
    actionText?: ToastConfig['actionText'];
    onActionClick?: ToastConfig['onActionClick'];
    filled?: ToastConfig['filled'];
  };
  createdAt: number;
};

type ToastContextType = {
  showToast: (message: string, config?: ToastConfig) => string;
  closeToast: (id: string) => void;
  closeAllToasts: () => void;
  toasts: ToastItem[];
};

const defaultConfig: Required<
  Omit<ToastConfig, 'icon' | 'actionText' | 'onActionClick' | 'filled'>
> & {
  icon?: ToastConfig['icon'];
  actionText?: ToastConfig['actionText'];
  onActionClick?: ToastConfig['onActionClick'];
  filled?: ToastConfig['filled'];
} = {
  type: 'info',
  autoClose: 5000,
  closeOnClick: true,
  filled: true
};

const ToastContext = createContext<ToastContextType>({
  showToast: () => '',
  closeToast: () => {},
  closeAllToasts: () => {},
  toasts: []
});

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback(
    (message: string, config?: ToastConfig): string => {
      const id = generateId();
      const toastConfig = { ...defaultConfig, ...config };

      const newToast: ToastItem = {
        id,
        message,
        config: toastConfig,
        createdAt: Date.now()
      };

      setToasts(prev => [...prev, newToast]);

      if (toastConfig.autoClose > 0) {
        setTimeout(() => {
          closeToast(id);
        }, toastConfig.autoClose);
      }

      return id;
    },
    [generateId]
  );

  const closeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const closeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        showToast,
        closeToast,
        closeAllToasts,
        toasts
      }}
    >
      <ToastContainer
        toasts={toasts.map(toast => ({
          id: toast.id,
          message: toast.message,
          type: toast.config.type,
          autoClose: toast.config.autoClose,
          closeOnClick: toast.config.closeOnClick,
          icon: toast.config.icon,
          actionText: toast.config.actionText,
          onActionClick: toast.config.onActionClick,
          filled: toast.config.filled,
          createdAt: toast.createdAt
        }))}
        onCloseToast={closeToast}
      />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};
